import React, { useState } from 'react';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

interface TimeSlot {
  from: string;
  to: string;
}

interface DaySchedule {
  day: string;
  slots: TimeSlot[];
}

interface BookingCalendarProps {
  timeZone: string;
  schedule: DaySchedule[];
  onTimeSlotSelect?: (date: dayjs.Dayjs, slot: TimeSlot) => void;
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  timeZone,
  schedule,
  onTimeSlotSelect
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);

  const getDaysInMonth = (date: dayjs.Dayjs) => {
    const daysInMonth = date.daysInMonth();
    const firstDayOfMonth = date.startOf('month');
    const lastDayOfMonth = date.endOf('month');
    
    const days = [];
    const startDay = firstDayOfMonth.day();
    
    // Add previous month's days
    for (let i = 0; i < startDay; i++) {
      days.push({
        date: firstDayOfMonth.subtract(startDay - i, 'day'),
        isCurrentMonth: false,
        isAvailable: false
      });
    }
    
    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = date.date(i);
      const dayOfWeek = currentDate.format('dddd');
      const daySchedule = schedule.find(s => s.day === dayOfWeek);
      const isAvailable = (daySchedule?.slots || []).length > 0;
      
      days.push({
        date: currentDate,
        isCurrentMonth: true,
        isAvailable,
        slots: daySchedule?.slots || []
      });
    }
    
    // Add next month's days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: lastDayOfMonth.add(i, 'day'),
        isCurrentMonth: false,
        isAvailable: false
      });
    }
    
    return days;
  };

  const generateTimeSlots = (startTime: string, endTime: string, duration: number = 30) => {
    const slots: string[] = [];
    let current = dayjs(startTime, 'h:mm A');
    const end = dayjs(endTime, 'h:mm A');

    while (current.isBefore(end)) {
      slots.push(current.format('h:mm A'));
      current = current.add(duration, 'minute');
    }

    return slots;
  };

  const getAvailableTimeSlots = (date: dayjs.Dayjs) => {
    const dayOfWeek = date.format('dddd');
    const daySchedule = schedule.find(s => s.day === dayOfWeek);
    
    if (!daySchedule || !daySchedule.slots.length) return [];
    
    const allSlots: string[] = [];
    daySchedule.slots.forEach(slot => {
      allSlots.push(...generateTimeSlots(slot.from, slot.to));
    });
    
    return allSlots;
  };

  const handleDateClick = (day: any) => {
    if (!day.isAvailable) return;
    setSelectedDate(day.date);
    setShowTimeSlots(true);
  };

  const handleBackToCalendar = () => {
    setShowTimeSlots(false);
    setSelectedDate(null);
  };

  const renderCalendar = () => {
    const days = getDaysInMonth(currentMonth);
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <>
        <div className="calendar-header">
          <Button 
            type="text" 
            icon={<LeftOutlined />}
            onClick={() => setCurrentMonth(prev => prev.subtract(1, 'month'))}
          />
          <span className="current-month">
            {currentMonth.format('MMMM YYYY')}
          </span>
          <Button 
            type="text" 
            icon={<RightOutlined />}
            onClick={() => setCurrentMonth(prev => prev.add(1, 'month'))}
          />
        </div>

        <div className="calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-days">
          {days.map((day, index) => (
            <div
              key={index}
              className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} 
                ${day.isAvailable ? 'available' : ''} 
                ${selectedDate?.isSame(day.date, 'day') ? 'selected' : ''}`}
              onClick={() => handleDateClick(day)}
            >
              {day.date.date()}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderTimeSlots = () => {
    if (!selectedDate) return null;
    const timeSlots = getAvailableTimeSlots(selectedDate);

    return (
      <div className="time-slots-view">
        <div className="time-slots-header">
          <Button 
            type="text" 
            icon={<LeftOutlined />}
            onClick={handleBackToCalendar}
          />
          <h4>Available Times for {selectedDate.format('ddd, MMMM D, YYYY')}</h4>
        </div>
        <div className="time-slots-grid">
          {timeSlots.map((time, index) => (
            <Button
              key={index}
              className="time-slot-button"
              onClick={() => onTimeSlotSelect?.(selectedDate, { from: time, to: '' })}
            >
              {time}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="booking-calendar">
      <div className="timezone-display">
        {timeZone.split('|')[0].trim()}
      </div>
      <div className="calendar-container">
        {showTimeSlots ? renderTimeSlots() : renderCalendar()}
      </div>
    </div>
  );
};

export default BookingCalendar; 